import storeManager from '../../store/StoreManager';
import sessionReader from './SessionReader';
import numberingSchemes from '../../constants/NumberingSchemes';
import testTypes from '../../constants/TestTypes';

/*
    "auto_deliver": boolean,
    "auto_request_plagiarism_control": boolean,
    "custom_lost_connection_alert_message": string
    "enable_alternative_feedback": boolean, // Whether or not to show feedback info for alternatives
    "enable_attached_sketches": boolean,
    "enable_candidate_highlighting": boolean,
    "enable_flagging": boolean,
    "enable_new_player": boolean,
    "enable_sketches": boolean,
    "hide_assessmentTrackDiv": boolean,
    "hide_question_titles": boolean,
    "lockdown_browser": boolean,
    "numbering_scheme": string, // sectionAlphaNumeric, sectionAlphaNumeric, testSequential, sectionNumeric
    "oral_exam": boolean,
    "qti2_hideResultpage": boolean, // Whether or not to show result page directly after delivery.
    "qti2_notNavigateBack": boolean,
    "qti2_student_preview": boolean, // Whether or not to make delivery open for review by student after delivery. (Link from Dashboard)
    "qti2_student_preview_full_feedback": boolean, // Whether or not to show results and correct answers for student review.
    "require_day_password": boolean,
    "show_correct_answers": boolean,
    "show_lost_connection_alert": boolean,
    "show_max_marks": boolean,
    "show_section": boolean,
    "spell_checker_disabled": boolean,
    "start_test_manually": boolean,
    "test_player_style": string, // default, primary_school_1, primary_school_2, primary_school_3
    "timeRemainingHidden": boolean,
*/

const settingsReader = {
    hasSettingValue(settingName, expectedValue, settings) {
        const settingsToUse = settings || storeManager.getState().settings;
        return settingsToUse.values && settingsToUse.values[settingName] === expectedValue;
    },

    getMarketplacePropertyValue(settingName, settings = storeManager.getState().settings) {
        const marketplaceProperties = { ...settings.marketplaceProperties, ...window.marketplaceProperties };
        return marketplaceProperties[settingName];
    },

    hasMarketplacePropertyValue(settingName, expectedValue) {
        return this.getMarketplacePropertyValue(settingName) === expectedValue;
    },

    isDeliverBlankEnabled(settings, api) {
        const state = storeManager.getState();
        const usedApi = api || state.api;
        return this.hasMarketplacePropertyValue('IA_DELIVER_BLANK_ENABLED', true, settings || state.settings)
            && usedApi.isOnline;
    },

    isCandidateLevelDurationEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_CANDIDATE_LEVEL_DURATION', true);
    },

    isEssayToolbarPresetEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PRESET_ESSAY_TOOLBAR', true);
    },

    isEssaySpecialCharsEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PRESET_ESSAY_SPECIAL_CHARS', true);
    },

    isMathSymbolPalettePresetEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_PRESET_MATH_SYMBOL_PALETTE', true);
    },

    isTTSEnabled(settings) {
        return this.hasSettingValue('enable_tts', true, settings || storeManager.getState().settings);
    },

    isSEBForIOSEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_SEB_IOS', true);
    },

    getPlayerAllowedResourceLoadTimeMinutes() {
        return this.getMarketplacePropertyValue('PLAYER_ALLOWED_RESOURCE_LOAD_TIME_MINUTES');
    },

    getPlayerMediaPresignedUrlLifetimeHours() {
        return this.getMarketplacePropertyValue('PLAYER_MEDIA_ITEMS_PRESIGNED_URL_LIFETIME') || 5;
    },

    isMathJax3Enabled() {
        return this.hasMarketplacePropertyValue('ENABLE_MATHJAX3_RENDER', true);
    },

    shouldUsePresignedUrl() {
        return this.hasMarketplacePropertyValue('PLAYER_USE_PRESIGNED_URL', true)
            || (sessionReader.isContextThumbnail());
    },

    isExpandableTextFieldEnabled() {
        return this.hasMarketplacePropertyValue('ENABLE_EXPANDABLE_TEXT_FIELD', true);
    },

    isFlaggingEnabled(settings) {
        return this.hasSettingValue('enable_flagging', true, settings || storeManager.getState().settings);
    },

    isShowMaxMarksEnabled(settings) {
        return this.hasSettingValue('show_max_marks', true, settings || storeManager.getState().settings);
    },

    isShowQuestionTitlesEnabled(settings) {
        return this.hasSettingValue('hide_question_titles', false, settings || storeManager.getState().settings);
    },

    isShowSectionEnabled(settings) {
        return this.hasSettingValue('show_section', true, settings || storeManager.getState().settings);
    },

    isCandidateHighlightingEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('enable_candidate_highlighting', true, settings);
    },

    isWithdrawEnabled(settings) {
        return this.hasMarketplacePropertyValue('IA_ENABLE_WITHDRAW', true, settings || storeManager.getState().settings);
    },

    isInvigilatorPasswordRequiredForDeliverBlank(settings) {
        return this.hasMarketplacePropertyValue('IA_DELIVER_BLANK_INVIGILATOR_PASSWORD', true, settings || storeManager.getState().settings);
    },

    isInvigilatorPasswordToResumeTestDisabled() {
        return this.hasMarketplacePropertyValue('DISABLE_INVIGILATOR_PASSWORD_TO_RESUME_OR_QUIT_TEST', true);
    },

    showAssessmentTrack(settings) {
        return this.hasSettingValue('hide_assessmentTrackDiv', false, settings || storeManager.getState().settings);
    },

    allowNavigateBack(settings) {
        return this.hasSettingValue('qti2_notNavigateBack', false, settings || storeManager.getState().settings);
    },

    isTimeRemainingHidden(settings = storeManager.getState().settings) {
        return this.hasSettingValue('timeRemainingHidden', true, settings);
    },

    showTableOfContents(settings) {
        return this.hasSettingValue('show_table_of_contents', true, settings || storeManager.getState().settings);
    },

    showAccessibilityHelpers(settings) {
        return this.hasSettingValue('show_accessibility_helpers', true, settings || storeManager.getState().settings);
    },

    showCorrectResponsesIfAvailable(settings) {
        return !this.hasSettingValue('show_correct_answers', false, settings || storeManager.getState().settings);
    },

    showCorrectResponsesPostDelivery(settings) {
        return this.hasSettingValue('qti2_student_preview_full_feedback', true, settings || storeManager.getState().settings);
    },

    areAttachedSketchesEnabled(settings) {
        return this.hasSettingValue('enable_attached_sketches', true, settings || storeManager.getState().settings);
    },

    /**
     * Checks in the marketplace property to enable feedback support is on.
     * @param settings
     * @returns {boolean}
     */
    isFeedbackEnabledInMarketplace(settings) {
        return this.hasMarketplacePropertyValue('IA_OFFER_ENABLE_FEEDBACK', true, settings || storeManager.getState().settings);
    },

    isServiceWorkerCacheEnabled(settings) {
        return this.hasMarketplacePropertyValue('ENABLE_PLAYER_SERVICE_WORKER_CACHE', true, settings || storeManager.getState().settings);
    },

    shouldCacheResourcesBeforeTestStart(settings = storeManager.getState().settings) {
        return this.hasSettingValue('preload_test_before_start', true, settings || storeManager.getState().settings);
    },

    /**
     * Checks if the particular Test has support for showing feedback for alternative.
     * @param settings
     * @returns {boolean}
     */
    isFeedbackForAlternativeEnabled(settings) {
        return this.hasSettingValue('enable_alternative_feedback', true, settings || storeManager.getState().settings);
    },

    /**
     * Checks if the particular Test has support for showing feedback for question.
     * @param settings
     * @returns {boolean}
     */
    isFeedbackForQuestionEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('enable_question_feedback', true, settings);
    },

    isInteractionFeedbackEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('enable_interaction_feedback', true, settings);
    },

    isShowResultsAndCorrectAnswersForStudentReviewEnabled(settings = storeManager.getState().settings) {
        return this.hasSettingValue('qti2_student_preview_full_feedback', true, settings);
    },

    isReceiptModeActive(settings = storeManager.getState().settings) {
        return this.hasSettingValue('qti2_student_preview', true, settings);
    },

    showLostConnectionAlert(settings = storeManager.getState().settings) {
        return this.hasSettingValue('show_lost_connection_alert', true, settings);
    },

    getCustomLostConnectionAlertMessage(settings = storeManager.getState().settings) {
        return settings.values.custom_lost_connection_alert_message?.replace(/\\"/g, '"');
    },

    hideNextButtonUntilAnswered(settings = storeManager.getState().settings) {
        return this.hasSettingValue('hide_next_button_until_question_answered', true, settings) || sessionReader.isContextReadinessCheck();
    },

    /**
     * Gets the maximum allowed file upload size in bytes.
     * @param settings
     * @returns {int}
     */
    getMaxFileUploadSize({ settings } = storeManager.getState()) {
        let megabytes = parseInt(settings.marketplaceProperties.S3_FILE_SIZE_THRESHOLD_MB, 10);
        megabytes = isNaN(megabytes) ? 1024 : megabytes;

        return parseInt(megabytes, 10) * 1024 * 1024;
    },

    getRewindAudioTimeout({ settings } = storeManager.getState()) {
        let timeout = parseInt(settings.marketplaceProperties.REWIND_AUDIO_TIMEOUT, 10);
        timeout = isNaN(timeout) ? 5000 : timeout;

        return parseInt(timeout, 10);
    },

    getSettingsForSection(sectionId, { settings } = storeManager.getState()) {
        const sectionsSettings = settings.values.section_settings || {};
        return sectionsSettings[sectionId] || {};
    },

    getNumberingScheme({ settings } = storeManager.getState()) {
        return settings.values.numbering_scheme;
    },

    useSectionAlphaNumeric() {
        return this.getNumberingScheme() === numberingSchemes.SECTION_ALPHA_NUMERIC;
    },

    useSectionNumeric() {
        return this.getNumberingScheme() === numberingSchemes.SECTION_NUMERIC;
    },

    useTestSequential() {
        return this.getNumberingScheme() === numberingSchemes.TEST_SEQUENTIAL;
    },

    useNumberingPerScorableItem() {
        return this.getNumberingScheme() === numberingSchemes.PER_SCORABLE_ITEM;
    },

    getTestPlayerStyle({ settings } = storeManager.getState()) {
        const testPlayerStyle = settings.values.test_player_style;
        if (testPlayerStyle === 'default') {
            return undefined;
        }

        return testPlayerStyle;
    },

    getLockdownAdditionalResources({ settings } = storeManager.getState()) {
        return settings.values.lockdown_additional_resources;
    },

    hasLockdownAdditionalResources({ settings } = storeManager.getState()) {
        return !!settings.values.lockdown_additional_resources;
    },

    isManualStart(settings = storeManager.getState().settings) {
        return !!settings.values.start_test_manually;
    },

    isChatEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_chat_during_test_taking;
    },

    isIdleStateWarningEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_idle_state_warning;
    },

    isRedirectUrlEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_redirect_to_url_after_submitting_demo_test;
    },

    getRedirectUrl() {
        const { settings } = storeManager.getState();
        return settings.values.redirect_to_url_after_submitting_demo_test;
    },

    getTestType({ settings } = storeManager.getState()) {
        return settings.values.atc_test_type;
    },

    isCatTestType() {
        return this.getTestType() === testTypes.CAT_TEST;
    },

    isLoftTestType() {
        return this.getTestType() === testTypes.LOFT_TEST;
    },

    isCatSyncMode() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_sync_mode_on_cat_test;
    },

    isCopyPasteDisabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.disable_copy_paste;
    },

    getCatTestAdditionalResources({ settings } = storeManager.getState()) {
        return settings.values.test_adaptive_steps;
    },

    hasCatTestAdditionalResources({ settings } = storeManager.getState()) {
        return !!settings.values.test_adaptive_steps;
    },

    allowCandidateTakeNotesDuringRemoteOralExam() {
        return true;
        // TODO: Implement this setting on platform (IA1-6160)
        // const { settings } = storeManager.getState();
        // return !!settings.values.allow_candidates_to_take_private_notes;
    },
    // Allow candidates to take private notes

    isWarningNavigatingFromIncompleteResponsesEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.warning_navigating_from_incomplete_responses;
    },

    isProgressBarQuestionsEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.progress_bar_questions;
    },

    isPromoteNextButtonOnDoneEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.promote_next_button_on_done;
    },

    isOneWayNavigationEnabled() {
        const { settings } = storeManager.getState();
        return !!settings.values.enable_one_way_navigation;
    },

    isOfflineOneWayNavigationDisallowed() {
        const { settings } = storeManager.getState();
        return !!settings.values.disallow_offline_one_way_navigation;
    },

    shouldOfferAnsweredQuestionsAutocommit() {
        const { settings } = storeManager.getState();
        return !!settings.values.offer_autocommit_of_answered_questions;
    },

    shouldPageBreakOnPrintView() {
        return this.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_BEFORE_QUESTION, true);
    },

    isShowRubricForQuestionEnabled() {
        return this.getMarketplacePropertyValue('ENABLE_REUSABLE_RUBRICS')
        && this.hasSettingValue('enable_rubrics_player_display', true);
    },
};

export default settingsReader;
